import { useState } from "react";
import { Flex, ModalBody, Stack, TagCloseButton, TagLabel, Wrap } from "@chakra-ui/react";
import { useTranslation } from "next-i18next";
import { LogoMonoIcon, ModalLegacyProps } from "@looksrare/uikit";
import { isAddressEqual, useGetFormattedErrorAndTitle, useSignInHandler } from "@looksrare/utils";
import { WatchlistCollectionSearchResult } from "types/graphql";
import { useAlgoliaCollectionIndexSearch } from "hooks/api/algolia/useAlgoliaCollectionIndexSearch";
import { useDebouncedSearch } from "hooks/useDebouncedSearch";
import { useAddToMainWatchlist, useSeedWatchlist } from "hooks/watchlists";
import { useToast } from "hooks/useToast";
import { Button, Tag, Text, TextInput } from "uikit";
import { SearchPrompt } from "components/Modals/AddCollectionToWatchlistModal/SearchPrompt";
import { CollectionResults } from "components/Modals/AddCollectionToWatchlistModal/CollectionResults";

interface AddCollectionToWatchlistModalBodyProps {
  onClose: ModalLegacyProps["onClose"];
}

export const AddCollectionToWatchlistModalBody = ({ onClose }: AddCollectionToWatchlistModalBodyProps) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const getFormattedErrorAndTitle = useGetFormattedErrorAndTitle();
  const { search, handleInputChange, clearSearch } = useDebouncedSearch();

  const [selectedCollections, setSelectedCollections] = useState<WatchlistCollectionSearchResult[]>([]);
  const toggleSelectedCollection = (clickedCollection: WatchlistCollectionSearchResult) => {
    const index = selectedCollections.findIndex((coll) => isAddressEqual(coll.address, clickedCollection.address));
    if (index >= 0) {
      // remove
      setSelectedCollections([...selectedCollections.slice(0, index), ...selectedCollections.slice(index + 1)]);
    } else {
      // add
      setSelectedCollections([...selectedCollections, clickedCollection]);
    }
  };

  const algoliaCollectionsQuery = useAlgoliaCollectionIndexSearch(search.debouncedValue, {
    enabled: !!search.debouncedValue,
  });

  const collections = algoliaCollectionsQuery.data || [];

  const { mutate: handleSeedWatchlist, isPending: isSeedInProgress } = useSeedWatchlist({
    onSuccess: () => {
      toast({
        title: t("Added to Watchlist"),
      });
      onClose();
    },
    onError: (error) => {
      const { title, description } = getFormattedErrorAndTitle(error);
      toast({ title, description, status: "error", dataIdSuffix: "watchlist-collection-seed" });
      onClose();
    },
  });
  const { mutate: addToMainWatchlist, isPending: isLoadingAddToWatchlist } = useAddToMainWatchlist({
    onSuccess: () => {
      toast({
        title: t("Added to Watchlist"),
      });
      onClose();
    },
    onError: (error) => {
      const { title, description } = getFormattedErrorAndTitle(error);
      toast({ title, description, status: "error", dataIdSuffix: "watchlist-col-main-add" });
    },
  });
  const { signInHandler: signInAndSeedWatchlist } = useSignInHandler({
    onAuthSuccess: () => handleSeedWatchlist(undefined),
  });
  const { signInHandler: signInAndAddToMain } = useSignInHandler({
    onAuthSuccess: () => addToMainWatchlist(selectedCollections.map((coll) => coll.address)),
  });

  return (
    <ModalBody bg="ui-01" borderRadius="dialog">
      <Flex flex={4} pb={2} px={2}>
        <TextInput
          bg="field-02"
          isActive={!!search.value}
          data-id="account-nfts-filters-text-input"
          wrapperProps={{ width: "100%", sx: { svg: { boxSize: 5 } } }}
          placeholder={t("Search Collections")}
          value={search.value}
          variant="lowcontrast"
          onChange={handleInputChange}
          onClear={clearSearch}
        />
      </Flex>
      {!search.debouncedValue ? (
        <SearchPrompt seedWatchlist={signInAndSeedWatchlist} isSeedInProgress={isSeedInProgress} />
      ) : (
        collections.length > 0 && (
          <CollectionResults
            collections={collections}
            toggleSelectedCollection={toggleSelectedCollection}
            selectedCollections={selectedCollections}
            minHeight="220px"
            maxHeight="300px"
          />
        )
      )}

      {/* No results */}
      {!!search.debouncedValue && !algoliaCollectionsQuery.isLoading && collections.length === 0 && (
        <Stack spacing={4} alignItems="center" py={6}>
          <LogoMonoIcon opacity="0.1" boxSize={24} />
          <Text color="text-02" bold>
            {t("No Results Found")}
          </Text>
          <Button onClick={clearSearch} variant="outline" colorScheme="secondary">
            {t("Clear Search")}
          </Button>
        </Stack>
      )}

      {/* Selected Tags and AddButton */}
      {selectedCollections.length > 0 && (
        <Stack spacing={2}>
          <>
            <Wrap spacing={2}>
              <Tag isClickable onClick={() => setSelectedCollections([])}>
                {t("Clear All")}
              </Tag>
              {selectedCollections.map((selectedCollection) => {
                return (
                  <Tag
                    key={selectedCollection.address}
                    isClickable
                    onClick={() => toggleSelectedCollection(selectedCollection)}
                  >
                    <TagLabel>{selectedCollection.name}</TagLabel>
                    <TagCloseButton />
                  </Tag>
                );
              })}
            </Wrap>
            <Button width="100%" onClick={signInAndAddToMain} isLoading={isLoadingAddToWatchlist}>
              {t("Add {{count}}", { count: selectedCollections.length })}
            </Button>
          </>
        </Stack>
      )}
    </ModalBody>
  );
};
